import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MdSettings, MdStorage, MdDelete, MdDownload, MdUpload, MdHistory,
  MdWarning, MdRefresh, MdCleaningServices, MdInfo, MdCategory, MdAdd, MdEdit, MdSave,
  MdSwapHoriz, MdSchedule, MdPlayArrow, MdBusiness, MdEmail,
} from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { confirm } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

// Settings sub-modules. The URL drives the active tab (/settings/:tab); when
// no tab is in the URL, we default to "general". Each tab is conceptually a
// mini-page so the operator isn't drowning in one giant scroll.
const SETTINGS_TABS = [
  { id: 'general',    label: 'Company Info', icon: <MdBusiness />          },
  { id: 'database',   label: 'Database',     icon: <MdStorage />           },
  { id: 'cleanup',    label: 'Cleanup',      icon: <MdCleaningServices />  },
  { id: 'lookups',    label: 'Categories & Reasons', icon: <MdCategory /> },
  { id: 'schedules',  label: 'Schedules',    icon: <MdSchedule />          },
  { id: 'activity',   label: 'Activity Log', icon: <MdHistory />           },
];

const Toast = ({ msg, type, onClose }) => (
  <div className={`toast toast-${type}`} style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    padding: '12px 18px', borderRadius: 8, color: 'white',
    background: type === 'error' ? '#d32f2f' : type === 'warning' ? '#f57c00' : '#2e7d32',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'flex', alignItems: 'center', gap: 8, maxWidth: 400,
  }}>
    {msg}
    <button onClick={onClose} style={{
      marginLeft: 8, background: 'none', border: 'none',
      color: 'white', cursor: 'pointer', fontSize: 18, lineHeight: 1,
    }}>×</button>
  </div>
);

const Section = ({ icon, title, subtitle, children }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <div className="card-header">
      <div>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon} {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{subtitle}</div>
        )}
      </div>
    </div>
    <div style={{ padding: '16px 20px' }}>{children}</div>
  </div>
);

const ConfirmModal = ({ open, title, body, danger, confirmText, onConfirm, onCancel, requireText }) => {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (open) setTyped(''); }, [open]);
  if (!open) return null;
  const ok = !requireText || typed === requireText;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
    }}>
      <div style={{
        background: 'white', borderRadius: 10, padding: 24, width: 480, maxWidth: '90%',
        boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          color: danger ? '#d32f2f' : '#1a3c6e', marginBottom: 12,
        }}>
          <MdWarning style={{ fontSize: 28 }} />
          <h3 style={{ margin: 0, fontSize: 18 }}>{title}</h3>
        </div>
        <div style={{ color: '#444', lineHeight: 1.5, marginBottom: 16 }}>{body}</div>
        {requireText && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>
              Type <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
                {requireText}
              </code> to confirm:
            </div>
            <input
              autoFocus
              value={typed}
              onChange={e => setTyped(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 6,
                border: '1px solid #ccc', fontSize: 14,
              }}
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            disabled={!ok}
            onClick={onConfirm}
            style={{ opacity: ok ? 1 : 0.5 }}
          >
            {confirmText || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  // Active sub-module — comes from the URL (/settings/:tab) or defaults.
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const tab = SETTINGS_TABS.find(t => t.id === tabParam)?.id || 'general';
  const goToTab = (id) => navigate(`/settings/${id}`);

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [toast, setToast] = useState(null);

  // Modals
  const [wipeOpen, setWipeOpen] = useState(false);
  const [keepAdmins, setKeepAdmins] = useState(true);

  // Cleanup form state
  const [auditDays, setAuditDays] = useState(90);
  const [rawDays, setRawDays] = useState(0);
  const [busy, setBusy] = useState(false);

  // Activity log
  const [activity, setActivity] = useState({ rows: [], total: 0 });
  const [actLimit] = useState(25);
  const [actOffset, setActOffset] = useState(0);
  const [actFilter, setActFilter] = useState({ action: '', target_type: '' });
  // 'activity' = user CRUD timeline, 'system' = auth/db/system. The mobile
  // and web split events the same way so the categories stay consistent.
  const [actCategory, setActCategory] = useState('activity');
  // Admin-only user picker. null = no filter; an int = user_id to scope to.
  const [actUserId, setActUserId] = useState(null);
  const [actUsers, setActUsers] = useState([]);
  // Email + restore-staging status — surfaces as banners so the operator
  // knows scheduled emails won't actually go out (or that a restore is
  // pending) instead of finding out hours later.
  const [emailStatus, setEmailStatus] = useState(null);
  const [restorePending, setRestorePending] = useState(null);
  const [emailTestRecipient, setEmailTestRecipient] = useState('');
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [loadingAct, setLoadingAct] = useState(false);

  // Categories
  const [categories, setCategories] = useState([]);
  const [catForm, setCatForm] = useState({ name: '', color: '#1565c0', description: '' });
  const [catEdit, setCatEdit] = useState(null);
  const [selectedCatIds, setSelectedCatIds] = useState(new Set());

  // Movement reasons
  const [reasons, setReasons] = useState([]);
  const [rForm, setRForm] = useState({ name: '', description: '', requires_approval: false });
  const [rEdit, setREdit] = useState(null);
  const [selectedReasonIds, setSelectedReasonIds] = useState(new Set());

  // Audit schedules
  const [schedules, setSchedules] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [sForm, setSForm] = useState({
    name: '', location_ids: [], include_descendants: false,
    frequency: 'weekly', day_of_week: 1, day_of_month: 1, hour: 9,
  });

  // Company info
  const [company, setCompany] = useState({
    company_name: '', company_address: '', company_phone: '',
    company_email: '', company_website: '', company_accent: '#1a3c6e',
  });
  const [companyDirty, setCompanyDirty] = useState(false);

  // Report schedules
  const [reportSchedules, setReportSchedules] = useState([]);
  const [rsForm, setRsForm] = useState({
    name: '', report_type: 'assets', format: 'excel',
    recipients: '', frequency: 'weekly', day_of_week: 1, day_of_month: 1, hour: 9,
  });

  // Restore
  const restoreRef = useRef();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const r = await api.get('/admin/db/stats');
      setStats(r.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load stats', 'error');
    } finally {
      setLoadingStats(false);
    }
  };

  const loadActivity = async () => {
    setLoadingAct(true);
    try {
      const params = { limit: actLimit, offset: actOffset, category: actCategory };
      if (actFilter.action) params.action = actFilter.action;
      if (actFilter.target_type) params.target_type = actFilter.target_type;
      if (actUserId) params.user_id = actUserId;
      const r = await api.get('/admin/activity', { params });
      setActivity(r.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load activity', 'error');
    } finally {
      setLoadingAct(false);
    }
  };

  // Roster for the admin's user filter — silently fails for non-admins.
  const loadActUsers = async () => {
    try {
      const r = await api.get('/users');
      setActUsers(r.data || []);
    } catch { /* non-admin → 403, ignore */ }
  };

  // SMTP config snapshot — drives the "email is/isn't configured" banner on
  // the Email Reports section + the "Send test email" button's enabled state.
  const loadEmailStatus = async () => {
    try { const r = await api.get('/admin/email/status'); setEmailStatus(r.data); }
    catch { /* non-admin → 403, ignore */ }
  };
  const loadRestoreStatus = async () => {
    try { const r = await api.get('/admin/db/restore-status'); setRestorePending(r.data); }
    catch { /* non-admin → 403, ignore */ }
  };
  const handleSendTestEmail = async () => {
    const to = (emailTestRecipient || currentUser?.email || '').trim();
    if (!to) { showToast('Enter a recipient address', 'error'); return; }
    try {
      const r = await api.post('/admin/email/test', { to });
      showToast(`Test email sent to ${to} (id: ${r.data.messageId?.slice(0, 24) || 'OK'})`);
    } catch (err) {
      showToast(err.response?.data?.error || 'Test email failed', 'error');
    }
  };
  const handleCancelRestore = async () => {
    if (!(await confirm('Cancel the staged restore? The current database keeps running unchanged.'))) return;
    try { await api.delete('/admin/db/restore'); loadRestoreStatus(); showToast('Staged restore cancelled'); }
    catch (err) { showToast(err.response?.data?.error || 'Cancel failed', 'error'); }
  };

  const loadCategories = async () => {
    try {
      const r = await api.get('/categories');
      setCategories(r.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load categories', 'error');
    }
  };

  const loadReasons = async () => {
    try {
      const r = await api.get('/movement-reasons');
      setReasons(r.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load reasons', 'error');
    }
  };

  const loadSchedules = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get('/audit-schedules'),
        api.get('/locations'),
      ]);
      setSchedules(s.data);
      setAllLocations(l.data);
    } catch (err) {
      // Non-admin users get 403 on /audit-schedules — silently skip
      if (err.response?.status !== 403) {
        showToast(err.response?.data?.error || 'Failed to load schedules', 'error');
      }
    }
  };

  const loadCompany = async () => {
    try {
      const r = await api.get('/app-settings');
      setCompany(prev => ({ ...prev, ...r.data }));
      setCompanyDirty(false);
    } catch {}
  };
  const saveCompany = async () => {
    try {
      await api.put('/app-settings', company);
      showToast('Company info saved');
      setCompanyDirty(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Save failed', 'error');
    }
  };

  const loadReportSchedules = async () => {
    try { const r = await api.get('/report-schedules'); setReportSchedules(r.data); }
    catch (err) { if (err.response?.status !== 403) showToast('Failed to load schedules', 'error'); }
  };
  const handleAddReportSchedule = async (e) => {
    e?.preventDefault();
    if (!rsForm.name.trim()) return showToast('Schedule name required', 'error');
    const recipients = rsForm.recipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) return showToast('At least one recipient email required', 'error');
    try {
      await api.post('/report-schedules', { ...rsForm, recipients });
      setRsForm({ name: '', report_type: 'assets', format: 'excel',
                  recipients: '', frequency: 'weekly', day_of_week: 1, day_of_month: 1, hour: 9 });
      showToast('Report schedule created');
      loadReportSchedules();
    } catch (err) { showToast(err.response?.data?.error || 'Add failed', 'error'); }
  };
  const handleRunReportNow = async (s) => {
    if (!(await confirm(`Run "${s.name}" now? Email will be sent if SMTP is configured.`))) return;
    try {
      const r = await api.post(`/report-schedules/${s.id}/run-now`);
      showToast(r.data.mail?.skipped
        ? `Report generated (${r.data.rows} rows) but email skipped: ${r.data.mail.reason}`
        : `Sent to ${r.data.recipients} recipient(s) (${r.data.rows} rows)`,
        r.data.mail?.skipped ? 'warning' : 'success');
      loadReportSchedules();
    } catch (err) { showToast(err.response?.data?.error || 'Run failed', 'error'); }
  };
  const handleDeleteReportSchedule = async (s) => {
    if (!(await confirm(`Delete "${s.name}"?`))) return;
    try { await api.delete(`/report-schedules/${s.id}`); loadReportSchedules(); showToast('Deleted'); }
    catch (err) { showToast('Delete failed', 'error'); }
  };

  useEffect(() => { loadStats(); loadActivity(); loadCategories(); loadReasons(); loadSchedules(); loadCompany(); loadReportSchedules(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadActivity(); /* eslint-disable-next-line */ }, [actOffset, actCategory, actUserId]);
  useEffect(() => { if (isAdmin) loadActUsers(); /* eslint-disable-next-line */ }, [isAdmin]);
  useEffect(() => { if (isAdmin) { loadEmailStatus(); loadRestoreStatus(); } /* eslint-disable-next-line */ }, [isAdmin]);

  // ── Audit schedules ─────────────────────────────────────────────────────────
  const handleAddSchedule = async (e) => {
    e?.preventDefault();
    if (!sForm.name.trim() || sForm.location_ids.length === 0) {
      showToast('Name and at least one location are required', 'error');
      return;
    }
    setBusy(true);
    try {
      await api.post('/audit-schedules', sForm);
      setSForm({ name: '', location_ids: [], include_descendants: false,
                 frequency: 'weekly', day_of_week: 1, day_of_month: 1, hour: 9 });
      showToast('Schedule created');
      loadSchedules();
    } catch (err) {
      showToast(err.response?.data?.error || 'Add failed', 'error');
    } finally { setBusy(false); }
  };
  const handleToggleSchedule = async (s) => {
    try {
      await api.put(`/audit-schedules/${s.id}`, { active: !s.active });
      loadSchedules();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    }
  };
  const handleDeleteSchedule = async (s) => {
    if (!(await confirm(`Delete schedule "${s.name}"?`))) return;
    try {
      await api.delete(`/audit-schedules/${s.id}`);
      showToast('Schedule deleted');
      loadSchedules();
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    }
  };
  const handleRunNow = async (s) => {
    if (!(await confirm(`Run "${s.name}" now? This creates a new audit immediately.`))) return;
    try {
      const r = await api.post(`/audit-schedules/${s.id}/run-now`);
      showToast(`Audit #${r.data.id} created`);
      loadSchedules();
    } catch (err) {
      showToast(err.response?.data?.error || 'Run failed', 'error');
    }
  };

  // ── Movement reasons ────────────────────────────────────────────────────────
  const handleAddReason = async (e) => {
    e?.preventDefault();
    if (!rForm.name.trim()) return;
    setBusy(true);
    try {
      await api.post('/movement-reasons', rForm);
      setRForm({ name: '', description: '', requires_approval: false });
      showToast('Reason added');
      loadReasons();
    } catch (err) {
      showToast(err.response?.data?.error || 'Add failed', 'error');
    } finally { setBusy(false); }
  };
  const handleSaveReasonEdit = async () => {
    if (!rEdit?.name?.trim()) return;
    setBusy(true);
    try {
      await api.put(`/movement-reasons/${rEdit.id}`, rEdit);
      setREdit(null);
      showToast('Reason updated');
      loadReasons();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    } finally { setBusy(false); }
  };
  const handleDeleteReason = async (r) => {
    if (!(await confirm(`Delete reason "${r.name}"?\n\nIf it's referenced by past movements, it'll be deactivated instead.`))) return;
    setBusy(true);
    try {
      const res = await api.delete(`/movement-reasons/${r.id}`);
      showToast(res.data.message || 'Reason deleted');
      loadReasons();
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    } finally { setBusy(false); }
  };

  // ── Categories ──────────────────────────────────────────────────────────────
  const handleAddCategory = async (e) => {
    e?.preventDefault();
    if (!catForm.name.trim()) return;
    setBusy(true);
    try {
      await api.post('/categories', catForm);
      setCatForm({ name: '', color: '#1565c0', description: '' });
      showToast('Category added');
      loadCategories();
    } catch (err) {
      showToast(err.response?.data?.error || 'Add failed', 'error');
    } finally { setBusy(false); }
  };

  const handleSaveCategoryEdit = async () => {
    if (!catEdit?.name?.trim()) return;
    setBusy(true);
    try {
      await api.put(`/categories/${catEdit.id}`, catEdit);
      setCatEdit(null);
      showToast('Category updated');
      loadCategories();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    } finally { setBusy(false); }
  };

  const handleDeleteCategory = async (cat) => {
    if (!(await confirm(`Delete category "${cat.name}"?`))) return;
    setBusy(true);
    try {
      await api.delete(`/categories/${cat.id}`);
      showToast('Category deleted');
      loadCategories();
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    } finally { setBusy(false); }
  };

  const handleBulkDeleteCategories = async () => {
    if (!selectedCatIds.size) return;
    if (!(await confirm(`Delete ${selectedCatIds.size} category(ies)? Categories still in use will be skipped.`))) return;
    setBusy(true);
    try {
      const r = await api.post('/categories/bulk-delete', { ids: Array.from(selectedCatIds) });
      const { deleted = 0, skipped = [] } = r.data || {};
      showToast(`Deleted ${deleted}` + (skipped.length ? ` · ${skipped.length} in use` : ''),
                skipped.length && !deleted ? 'error' : 'success');
      setSelectedCatIds(new Set());
      loadCategories();
    } catch (err) { showToast(err.response?.data?.error || 'Bulk delete failed', 'error'); }
    finally { setBusy(false); }
  };

  const handleBulkDeleteReasons = async () => {
    if (!selectedReasonIds.size) return;
    if (!(await confirm(`Delete ${selectedReasonIds.size} reason(s)? Reasons still in use will be deactivated instead of deleted.`))) return;
    setBusy(true);
    try {
      const r = await api.post('/movement-reasons/bulk-delete', { ids: Array.from(selectedReasonIds) });
      const { deleted = 0, deactivated = 0 } = r.data || {};
      showToast(`Deleted ${deleted}` + (deactivated ? ` · ${deactivated} deactivated (in use)` : ''));
      setSelectedReasonIds(new Set());
      loadReasons();
    } catch (err) { showToast(err.response?.data?.error || 'Bulk delete failed', 'error'); }
    finally { setBusy(false); }
  };

  const toggleCatSelect = (id) => setSelectedCatIds(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const toggleAllCats = () => setSelectedCatIds(prev =>
    prev.size === categories.length ? new Set() : new Set(categories.map(c => c.id)));
  const toggleReasonSelect = (id) => setSelectedReasonIds(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const toggleAllReasons = () => setSelectedReasonIds(prev =>
    prev.size === reasons.length ? new Set() : new Set(reasons.map(r => r.id)));

  // ── Wipe ────────────────────────────────────────────────────────────────────
  const handleWipe = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/db/wipe', { confirm: 'WIPE', keepAdmins });
      showToast(`Database wiped. Removed: ${Object.entries(r.data.beforeCounts).map(([k, v]) => `${k}=${v}`).join(', ')}`);
      setWipeOpen(false);
      loadStats();
      loadActivity();
    } catch (err) {
      showToast(err.response?.data?.error || 'Wipe failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── Backup ──────────────────────────────────────────────────────────────────
  const handleBackup = async () => {
    setBusy(true);
    try {
      const r = await api.get('/admin/db/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `asset-management-${ts}.db`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Backup downloaded');
      loadActivity();
    } catch (err) {
      showToast(err.response?.data?.error || 'Backup failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── Restore ─────────────────────────────────────────────────────────────────
  // Now stages the upload as `identium.db.pending-restore` instead of crashing
  // the backend. The swap happens on the next manual restart, which avoids
  // leaving the server dead when no process supervisor (pm2/systemd) is around.
  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!(await confirm(`Restore from "${file.name}"? The current database keeps running until you restart the backend.`))) return;

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('backup', file);
      const r = await api.post('/admin/db/restore', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(r.data.message, 'warning');
      loadRestoreStatus();
    } catch (err) {
      showToast(err.response?.data?.error || 'Restore failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── Cleanup ─────────────────────────────────────────────────────────────────
  const handleCleanupAudits = async () => {
    if (!(await confirm(`Delete completed audits older than ${auditDays} days?`))) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/audits/cleanup', { olderThanDays: auditDays });
      showToast(`Removed ${r.data.deletedAudits} audit(s) and ${r.data.deletedItems} item(s)`);
      loadStats();
      loadActivity();
    } catch (err) {
      showToast(err.response?.data?.error || 'Cleanup failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleClearRaw = async () => {
    const msg = rawDays > 0
      ? `Clear raw inventory older than ${rawDays} days?`
      : 'Clear ALL raw inventory? This cannot be undone.';
    if (!(await confirm(msg))) return;
    setBusy(true);
    try {
      const body = rawDays > 0 ? { olderThanDays: rawDays } : {};
      const r = await api.post('/admin/raw-inventory/clear', body);
      showToast(`Removed ${r.data.deleted} raw inventory record(s)`);
      loadStats();
      loadActivity();
    } catch (err) {
      showToast(err.response?.data?.error || 'Clear failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Clear logs for the currently-selected category. Backend rejects with 403
  // if the caller isn't admin; the button is also visually hidden when
  // !isAdmin. Older-than-30 mode kept as a separate button.
  const handleClearActivity = async (mode = 'all') => {
    const label = actCategory === 'system' ? 'System' : 'User Activity';
    const msg = mode === 'older30'
      ? `Clear ${label} entries older than 30 days?`
      : `Permanently delete ALL ${activity.total} ${label} entries? This cannot be undone.`;
    if (!(await confirm(msg))) return;
    setBusy(true);
    try {
      const params = { category: actCategory };
      if (mode === 'older30') params.olderThanDays = 30;
      const qs = new URLSearchParams(params).toString();
      const r = await api.delete(`/admin/activity?${qs}`);
      showToast(`Removed ${r.data.deleted} ${label} entries`);
      loadStats();
      setActOffset(0);
      loadActivity();
    } catch (err) {
      showToast(err.response?.data?.error || 'Clear failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const fmtDt = s => s ? new Date(s).toLocaleString() : '—';

  // Server-supplied severity → row tint. Soft backgrounds keep the table
  // readable while the rail + action label use the saturated colour.
  const severityColor = (severity) => {
    switch (severity) {
      case 'danger':  return { fg: '#d32f2f', bg: '#ffebee', rail: '#d32f2f' };
      case 'warning': return { fg: '#ef6c00', bg: '#fff3e0', rail: '#ef6c00' };
      case 'success': return { fg: '#2e7d32', bg: '#e8f5e9', rail: '#2e7d32' };
      case 'info':    return { fg: '#1565c0', bg: '#e3f2fd', rail: '#1565c0' };
      default:        return { fg: '#555',    bg: 'transparent', rail: '#cfd6e0' };
    }
  };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div>
          <div className="page-title"><MdSettings /> Settings &amp; Admin Tools</div>
          <div className="page-subtitle">{SETTINGS_TABS.find(t=>t.id===tab)?.label}</div>
        </div>
        <button className="btn btn-outline" onClick={() => { loadStats(); loadActivity(); }} disabled={busy}>
          <MdRefresh /> Refresh
        </button>
      </div>

      {/* Sub-module tab strip — clicking changes the URL so the active tab is
          shareable / refresh-stable. */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)',
        overflowX: 'auto', whiteSpace: 'nowrap',
      }} className="tabs-row">
        {SETTINGS_TABS.map(t => (
          <button key={t.id} onClick={() => goToTab(t.id)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 16px', background: 'transparent',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              marginBottom: -1,
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Company info / branding ───────────────────────────────────────────── */}
      {tab === 'general' && <Section
        icon={<MdBusiness style={{ color: 'var(--accent)' }} />}
        title="Company Info"
        subtitle="Appears on PDF report headers and Excel cover rows"
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          {[
            { k: 'company_name',    label: 'Company name', placeholder: 'Acme RFID Corp' },
            { k: 'company_address', label: 'Address',      placeholder: '123 Tag Lane' },
            { k: 'company_phone',   label: 'Phone',        placeholder: '+91 22 1234 5678' },
            { k: 'company_email',   label: 'Email',        placeholder: 'contact@example.com' },
            { k: 'company_website', label: 'Website',      placeholder: 'https://example.com' },
          ].map(f => (
            <div key={f.k}>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>{f.label}</label>
              <input
                type="text"
                value={company[f.k] || ''}
                placeholder={f.placeholder}
                onChange={e => { setCompany(c => ({ ...c, [f.k]: e.target.value })); setCompanyDirty(true); }}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
              />
            </div>
          ))}
          <div>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Accent color</label>
            <input
              type="color"
              value={company.company_accent || '#1a3c6e'}
              onChange={e => { setCompany(c => ({ ...c, company_accent: e.target.value })); setCompanyDirty(true); }}
              style={{ display: 'block', width: '100%', height: 38, border: '1px solid #ccc', borderRadius: 6 }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-primary" disabled={!companyDirty || busy} onClick={saveCompany}>
            <MdSave /> Save
          </button>
          {companyDirty && <span style={{ fontSize: 12, color: '#ef6c00' }}>unsaved changes</span>}
        </div>

        {/* Per-tenant logo upload. Replaces the generic icon in the sidebar,
            top bar, and on every PDF / Excel export for this company.
            See the spec lines below the heading for accepted formats /
            sizes — we surface them prominently because operators have
            been confused about what to upload. */}
        <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px dashed #d4dae4' }} />
        <h4 style={{ margin: '0 0 4px', fontSize: 14, color: '#1a3c6e', fontWeight: 700 }}>Company Logo</h4>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#546e7a' }}>
          Replaces the generic icon in the sidebar, top bar, and PDF / Excel export headers.
        </p>

        {/* Spec card — exactly what the user asked to see up front */}
        <div style={{
          background: '#f0f4f8', border: '1px solid #d4dae4', borderRadius: 8,
          padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#37474f',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            <div><strong>Formats:</strong> PNG, JPG, SVG, WebP</div>
            <div><strong>Max file size:</strong> 4 MB</div>
            <div><strong>Recommended:</strong> 400×200 px transparent PNG/SVG</div>
            <div><strong>Storage:</strong> 1 logo per company (replaces previous on upload)</div>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: '#78909c' }}>
            Anything larger is auto-resized to fit a header — but starting from a high-res
            source keeps it crisp. SVG renders perfectly at any zoom.
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
          {/* Preview — bigger so the user can actually judge how it looks */}
          <div style={{
            width: 200, height: 100, border: '1.5px dashed #cfd8dc', borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#fff', overflow: 'hidden', flexShrink: 0,
          }}>
            {company.company_logo_path ? (
              <img
                src={`${company.company_logo_path}?token=${encodeURIComponent(localStorage.getItem('identium_token') || '')}`}
                alt=""
                style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
                onError={(e) => {
                  // Hide broken image so the user doesn't see the alt text
                  // (which used to look like fake placeholder text). Show
                  // a real "image failed" hint instead.
                  e.currentTarget.style.display = 'none';
                  const sib = e.currentTarget.nextElementSibling;
                  if (sib) sib.style.display = 'flex';
                }}
              />
            ) : null}
            <span style={{
              display: company.company_logo_path ? 'none' : 'flex',
              alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
              fontSize: 11, color: '#90a4ae', textAlign: 'center', padding: 8,
            }}>
              <span style={{ fontSize: 22 }}>+</span>
              <span>No logo uploaded</span>
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 220 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#546e7a', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {company.company_logo_path ? 'Replace logo' : 'Upload logo'}
            </label>
            <input
              type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
              style={{ fontSize: 13 }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 4 * 1024 * 1024) {
                  showToast(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — max is 4 MB`, 'error');
                  e.target.value = '';
                  return;
                }
                const fd = new FormData(); fd.append('logo', file);
                try {
                  const r = await api.post('/app-settings/logo', fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } });
                  setCompany(c => ({ ...c, company_logo_path: r.data.company_logo_path }));
                  showToast('Logo uploaded — refresh the page to see it in the sidebar / header');
                } catch (err) {
                  showToast(err.response?.data?.error || 'Upload failed', 'error');
                } finally {
                  e.target.value = '';
                }
              }}
            />
            {company.company_logo_path && (
              <button
                className="btn btn-outline btn-sm"
                style={{ alignSelf: 'flex-start' }}
                onClick={async () => {
                  if (!(await confirm('Remove the company logo? The fallback icon will be shown instead.'))) return;
                  try {
                    await api.delete('/app-settings/logo');
                    setCompany(c => ({ ...c, company_logo_path: '' }));
                    showToast('Logo removed — refresh the page to update the sidebar');
                  } catch (err) {
                    showToast(err.response?.data?.error || 'Remove failed', 'error');
                  }
                }}
              >Remove logo</button>
            )}
          </div>
        </div>
      </Section>}

      {/* ── Database Stats ────────────────────────────────────────────────────── */}
      {tab === 'database' && <Section
        icon={<MdInfo style={{ color: 'var(--accent)' }} />}
        title="Database Snapshot"
        subtitle="Live row counts and on-disk size"
      >
        {loadingStats ? (
          <div style={{ color: '#888' }}>Loading…</div>
        ) : !stats ? (
          <div style={{ color: '#888' }}>No data</div>
        ) : (
          <>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 10, marginBottom: 12,
            }}>
              {Object.entries(stats.counts).map(([k, v]) => (
                <div key={k} style={{
                  background: '#f8f9fb', border: '1px solid #e0e4eb',
                  borderRadius: 8, padding: '10px 14px',
                }}>
                  <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {k.replace(/_/g, ' ')}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1a3c6e' }}>{v.toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>
              File size: <strong>{stats.fileSizeMB} MB</strong> ·
              {' '}Path: <code style={{ fontSize: 11 }}>{stats.dbPath}</code>
            </div>
          </>
        )}
      </Section>}

      {/* ── Backup / Restore ──────────────────────────────────────────────────── */}
      {tab === 'database' && <Section
        icon={<MdStorage style={{ color: 'var(--accent)' }} />}
        title="Backup &amp; Restore"
        subtitle="Download a snapshot of the entire database, or restore from a previous backup"
      >
        {/* Pending-restore banner — shows when an upload has been validated
            and staged. The current DB keeps running until the operator
            manually restarts the backend. Cancel-able. */}
        {restorePending?.pending && (
          <div style={{ background: '#fff3e0', border: '1px solid #ffcc80', color: '#bf360c', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong>Restore pending.</strong>
            <span>A new database file ({Math.round((restorePending.sizeBytes || 0) / 1024)} KB) was staged at {new Date(restorePending.stagedAt).toLocaleString()}.
              The current database is still live. Restart the backend to apply.</span>
            <button className="btn btn-outline btn-sm" onClick={handleCancelRestore} disabled={busy}>Cancel staged restore</button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={handleBackup} disabled={busy}>
            <MdDownload /> Download Backup (.db)
          </button>
          <input ref={restoreRef} type="file" accept=".db,.sqlite,.sqlite3"
            onChange={handleRestoreFile} style={{ display: 'none' }} />
          <button className="btn btn-warning" onClick={() => restoreRef.current?.click()} disabled={busy}>
            <MdUpload /> Restore from Backup…
          </button>
          <div style={{ fontSize: 12, color: '#777', flex: 1 }}>
            Restore validates the upload and stages it. The new database is applied on the next backend restart —
            no data lost in between. A pre-restore snapshot is saved automatically.
          </div>
        </div>
      </Section>}

      {/* ── Cleanup Tools ─────────────────────────────────────────────────────── */}
      {tab === 'cleanup' && <Section
        icon={<MdCleaningServices style={{ color: 'var(--accent)' }} />}
        title="Cleanup Tools"
        subtitle="Trim old or no-longer-needed records"
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          {/* Audits */}
          <div style={{ border: '1px solid #e0e4eb', borderRadius: 8, padding: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Old Audits</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
              Delete completed audits older than this many days (along with their items).
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="number" min={1} value={auditDays}
                onChange={e => setAuditDays(parseInt(e.target.value, 10) || 1)}
                style={{ width: 80, padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
              />
              <span style={{ fontSize: 13, color: '#666' }}>days</span>
              <button className="btn btn-outline btn-sm" onClick={handleCleanupAudits} disabled={busy}>
                <MdDelete /> Delete
              </button>
            </div>
          </div>

          {/* Raw inventory */}
          <div style={{ border: '1px solid #e0e4eb', borderRadius: 8, padding: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Raw Inventory</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
              Clear raw scan logs. Use 0 days to clear everything.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="number" min={0} value={rawDays}
                onChange={e => setRawDays(parseInt(e.target.value, 10) || 0)}
                style={{ width: 80, padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
              />
              <span style={{ fontSize: 13, color: '#666' }}>days {rawDays === 0 && '(all)'}</span>
              <button className="btn btn-outline btn-sm" onClick={handleClearRaw} disabled={busy}>
                <MdDelete /> Clear
              </button>
            </div>
          </div>

          {/* Activity log — admin-only clear, scoped to current tab */}
          <div style={{ border: '1px solid #e0e4eb', borderRadius: 8, padding: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Activity Log</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
              {isAdmin
                ? `Clear actions affect the currently selected tab (${actCategory === 'system' ? 'System' : 'User Activity'}).`
                : 'Only admins can clear logs.'}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-outline btn-sm" onClick={() => handleClearActivity('older30')} disabled={busy || !isAdmin}>
                <MdDelete /> Trim &gt; 30 days
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => handleClearActivity('all')} disabled={busy || !isAdmin}>
                <MdDelete /> Clear all
              </button>
            </div>
          </div>
        </div>
      </Section>}

      {/* ── Asset Categories ──────────────────────────────────────────────────── */}
      {tab === 'lookups' && <Section
        icon={<MdCategory style={{ color: 'var(--accent)' }} />}
        title="Asset Categories"
        subtitle="Managed list used in Asset Registration and All Assets dropdowns"
      >
        <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Name</label>
            <input
              type="text" value={catForm.name}
              onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. IT Equipment"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Color</label>
            <input
              type="color" value={catForm.color}
              onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))}
              style={{ display: 'block', width: 56, height: 36, border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer' }}
            />
          </div>
          <div style={{ flex: '2 1 240px' }}>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description (optional)</label>
            <input
              type="text" value={catForm.description}
              onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
            />
          </div>
          <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>
            <MdAdd /> Add
          </button>
        </form>

        {selectedCatIds.size > 0 && (
          <div style={{ padding: '8px 12px', background: 'var(--surface-2, #f5f5f5)', borderRadius: 6, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedCatIds.size} selected</span>
            <button className="btn btn-danger btn-sm" onClick={handleBulkDeleteCategories} disabled={busy}>
              <MdDelete /> Delete selected
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setSelectedCatIds(new Set())}>Clear</button>
          </div>
        )}
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox"
                    checked={categories.length > 0 && selectedCatIds.size === categories.length}
                    onChange={toggleAllCats} />
                </th>
                <th style={{ width: 40 }}>Color</th>
                <th>Name</th>
                <th>Description</th>
                <th style={{ width: 100 }}>Assets</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 20 }}>No categories yet</td></tr>
              ) : categories.map(c => (
                <tr key={c.id}>
                  <td>
                    <input type="checkbox"
                      checked={selectedCatIds.has(c.id)}
                      onChange={() => toggleCatSelect(c.id)} />
                  </td>
                  <td>
                    {catEdit?.id === c.id ? (
                      <input type="color" value={catEdit.color || '#1565c0'}
                        onChange={e => setCatEdit(m => ({ ...m, color: e.target.value }))}
                        style={{ width: 36, height: 28, border: '1px solid #ccc', borderRadius: 4, padding: 0 }} />
                    ) : (
                      <span style={{
                        display: 'inline-block', width: 24, height: 24, borderRadius: '50%',
                        background: c.color || '#999', border: '1px solid #ddd',
                      }} />
                    )}
                  </td>
                  <td>
                    {catEdit?.id === c.id ? (
                      <input value={catEdit.name}
                        onChange={e => setCatEdit(m => ({ ...m, name: e.target.value }))}
                        style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, width: '100%' }} />
                    ) : (
                      <span style={{ fontWeight: 500 }}>{c.name}</span>
                    )}
                  </td>
                  <td>
                    {catEdit?.id === c.id ? (
                      <input value={catEdit.description || ''}
                        onChange={e => setCatEdit(m => ({ ...m, description: e.target.value }))}
                        style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, width: '100%' }} />
                    ) : (
                      <span style={{ color: '#666', fontSize: 13 }}>{c.description || '—'}</span>
                    )}
                  </td>
                  <td><span className="badge badge-secondary">{c.asset_count}</span></td>
                  <td>
                    {catEdit?.id === c.id ? (
                      <>
                        <button className="btn btn-success btn-icon btn-sm" onClick={handleSaveCategoryEdit} disabled={busy} title="Save"><MdSave /></button>
                        <button className="btn btn-outline btn-icon btn-sm" onClick={() => setCatEdit(null)} disabled={busy} title="Cancel">×</button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-accent btn-icon btn-sm" onClick={() => setCatEdit({ ...c })} title="Edit"><MdEdit /></button>
                        <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDeleteCategory(c)} disabled={c.asset_count > 0} title={c.asset_count > 0 ? `${c.asset_count} asset(s) still use this — reassign first` : 'Delete'}><MdDelete /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>}

      {/* ── Audit Schedules ───────────────────────────────────────────────────── */}
      {tab === 'schedules' && <Section
        icon={<MdSchedule style={{ color: 'var(--accent)' }} />}
        title="Audit Schedules"
        subtitle="Recurring audits — automatically created at the configured cadence. The scheduler checks every minute."
      >
        <form onSubmit={handleAddSchedule} style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px auto', gap: 8, alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Name</label>
              <input type="text" value={sForm.name}
                onChange={e => setSForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Weekly warehouse audit"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Frequency</label>
              <SearchableSelect value={sForm.frequency}
                onChange={e => setSForm(f => ({ ...f, frequency: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </SearchableSelect>
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {sForm.frequency === 'weekly' ? 'Day of week' : sForm.frequency === 'monthly' ? 'Day of month' : '—'}
              </label>
              {sForm.frequency === 'weekly' ? (
                <SearchableSelect value={sForm.day_of_week}
                  onChange={e => setSForm(f => ({ ...f, day_of_week: Number(e.target.value) }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}>
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) =>
                    <option key={i} value={i}>{d}</option>)}
                </SearchableSelect>
              ) : sForm.frequency === 'monthly' ? (
                <input type="number" min={1} max={28} value={sForm.day_of_month}
                  onChange={e => setSForm(f => ({ ...f, day_of_month: Number(e.target.value) }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }} />
              ) : (
                <input disabled value="—" style={{ width: '100%', padding: '8px 10px', border: '1px solid #eee', borderRadius: 6, color: '#aaa' }} />
              )}
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Hour</label>
              <input type="number" min={0} max={23} value={sForm.hour}
                onChange={e => setSForm(f => ({ ...f, hour: Number(e.target.value) }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }} />
            </div>
            <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>
              <MdAdd /> Add
            </button>
          </div>

          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Locations</div>
            <div style={{
              border: '1px solid #ccc', borderRadius: 6, padding: 6, maxHeight: 120, overflowY: 'auto',
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2,
            }}>
              {allLocations.length === 0 ? (
                <div style={{ color: '#888', padding: 8 }}>No locations yet</div>
              ) : allLocations.map(l => (
                <label key={l.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
                  cursor: 'pointer', borderRadius: 4, fontSize: 12,
                  background: sForm.location_ids.includes(l.id) ? '#e8edf5' : 'transparent',
                }}>
                  <input type="checkbox" checked={sForm.location_ids.includes(l.id)}
                    onChange={() => setSForm(f => ({
                      ...f,
                      location_ids: f.location_ids.includes(l.id)
                        ? f.location_ids.filter(x => x !== l.id)
                        : [...f.location_ids, l.id],
                    }))} />
                  <code style={{ fontSize: 10 }}>{l.location_code}</code>
                  {l.location_name}
                </label>
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={sForm.include_descendants}
                onChange={e => setSForm(f => ({ ...f, include_descendants: e.target.checked }))} />
              Include sub-locations
            </label>
          </div>
        </form>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Locations</th>
                <th>Schedule</th>
                <th>Next run</th>
                <th>Last run</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 20 }}>No schedules yet</td></tr>
              ) : schedules.map(s => {
                const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                const cadence = s.frequency === 'daily'
                  ? `Daily at ${s.hour}:00`
                  : s.frequency === 'weekly'
                    ? `Every ${days[s.day_of_week]} at ${s.hour}:00`
                    : `Day ${s.day_of_month} of every month at ${s.hour}:00`;
                return (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.name}</td>
                    <td style={{ fontSize: 12 }}>
                      {s.location_ids.length} location(s)
                      {s.include_descendants && <span style={{ color: '#888' }}> + sub</span>}
                    </td>
                    <td style={{ fontSize: 12 }}>{cadence}</td>
                    <td style={{ fontSize: 12, color: '#666' }}>
                      {s.next_run_at ? new Date(s.next_run_at + ' UTC').toLocaleString() : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#666' }}>
                      {s.last_run_at ? new Date(s.last_run_at + ' UTC').toLocaleString() : '—'}
                    </td>
                    <td>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={s.active}
                          onChange={() => handleToggleSchedule(s)} />
                      </label>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-success btn-icon btn-sm" title="Run now"
                          onClick={() => handleRunNow(s)}><MdPlayArrow /></button>
                        <button className="btn btn-danger btn-icon btn-sm" title="Delete"
                          onClick={() => handleDeleteSchedule(s)}><MdDelete /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>}

      {/* ── Report Schedules ──────────────────────────────────────────────────── */}
      {tab === 'schedules' && <Section
        icon={<MdEmail style={{ color: 'var(--accent)' }} />}
        title="Scheduled Email Reports"
        subtitle="Generate Excel/PDF reports on a recurring schedule and email them to recipients. Requires SMTP configured in backend/.env."
      >
        {/* Email config status banner — without this the user creates a
            schedule, waits for the email, and never gets one because SMTP
            isn't actually wired up. */}
        {emailStatus && !emailStatus.configured && (
          <div style={{ background: '#fff3e0', border: '1px solid #ffcc80', color: '#ef6c00', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
            <strong>Email is not configured.</strong> Scheduled reports + audit-completion emails will be silently skipped.
            Set <code>SMTP_HOST</code>, <code>SMTP_PORT</code>, <code>SMTP_USER</code>, <code>SMTP_PASS</code>, <code>SMTP_FROM</code> in <code>backend/.env</code> and restart.
            <div style={{ marginTop: 6, fontSize: 12, color: '#7a4a00' }}>
              Free options: Gmail SMTP with an app password (500/day), Brevo free tier (300/day), Resend free tier (100/day).
            </div>
          </div>
        )}
        {emailStatus && emailStatus.configured && (
          <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span><strong>Email is configured</strong> — sending from <code>{emailStatus.fromAddress}</code> via <code>{emailStatus.host}:{emailStatus.port}</code>.</span>
            <input
              type="email"
              placeholder={currentUser?.email || 'recipient@example.com'}
              value={emailTestRecipient}
              onChange={e => setEmailTestRecipient(e.target.value)}
              style={{ flex: '1 1 200px', padding: '6px 10px', border: '1px solid #a5d6a7', borderRadius: 6, fontSize: 12 }}
            />
            <button className="btn btn-outline btn-sm" onClick={handleSendTestEmail}>Send test</button>
          </div>
        )}
        <form onSubmit={handleAddReportSchedule} style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 90px auto', gap: 8, alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Schedule name</label>
              <input type="text" value={rsForm.name}
                onChange={e => setRsForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Weekly asset register"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Report type</label>
              <SearchableSelect value={rsForm.report_type}
                onChange={e => setRsForm(f => ({ ...f, report_type: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}>
                <option value="assets">Assets</option>
                <option value="movements">Movements</option>
                <option value="raw_inventory">Raw inventory</option>
                <option value="audits">Audits</option>
              </SearchableSelect>
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Format</label>
              <SearchableSelect value={rsForm.format}
                onChange={e => setRsForm(f => ({ ...f, format: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}>
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
              </SearchableSelect>
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Frequency</label>
              <SearchableSelect value={rsForm.frequency}
                onChange={e => setRsForm(f => ({ ...f, frequency: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </SearchableSelect>
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {rsForm.frequency === 'weekly' ? 'Day' : rsForm.frequency === 'monthly' ? 'Day of month' : '—'}
              </label>
              {rsForm.frequency === 'weekly' ? (
                <SearchableSelect value={rsForm.day_of_week}
                  onChange={e => setRsForm(f => ({ ...f, day_of_week: Number(e.target.value) }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}>
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) =>
                    <option key={i} value={i}>{d}</option>)}
                </SearchableSelect>
              ) : rsForm.frequency === 'monthly' ? (
                <input type="number" min={1} max={28} value={rsForm.day_of_month}
                  onChange={e => setRsForm(f => ({ ...f, day_of_month: Number(e.target.value) }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }} />
              ) : (
                <input disabled value="—" style={{ width: '100%', padding: '8px 10px', border: '1px solid #eee', borderRadius: 6, color: '#aaa' }} />
              )}
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Hour</label>
              <input type="number" min={0} max={23} value={rsForm.hour}
                onChange={e => setRsForm(f => ({ ...f, hour: Number(e.target.value) }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }} />
            </div>
            <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>
              <MdAdd /> Add
            </button>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Recipients (comma-separated emails)
            </label>
            <input type="text" value={rsForm.recipients}
              onChange={e => setRsForm(f => ({ ...f, recipients: e.target.value }))}
              placeholder="alice@example.com, bob@example.com"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
            />
          </div>
        </form>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Report</th>
                <th>Format</th>
                <th>Recipients</th>
                <th>Cadence</th>
                <th>Next run</th>
                <th>Last run</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reportSchedules.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888', padding: 20 }}>No report schedules yet</td></tr>
              ) : reportSchedules.map(s => {
                const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                const cadence = s.frequency === 'daily'
                  ? `Daily ${s.hour}:00`
                  : s.frequency === 'weekly'
                    ? `${days[s.day_of_week]} ${s.hour}:00`
                    : `Day ${s.day_of_month} ${s.hour}:00`;
                return (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.name}</td>
                    <td><span className="badge badge-info">{s.report_type}</span></td>
                    <td>{s.format.toUpperCase()}</td>
                    <td style={{ fontSize: 12 }}>{s.recipients.join(', ')}</td>
                    <td style={{ fontSize: 12 }}>{cadence}</td>
                    <td style={{ fontSize: 11, color: '#666' }}>{s.next_run_at ? new Date(s.next_run_at + ' UTC').toLocaleString() : '—'}</td>
                    <td style={{ fontSize: 11, color: '#666' }}>{s.last_run_at ? new Date(s.last_run_at + ' UTC').toLocaleString() : '—'}</td>
                    <td>
                      <input type="checkbox" checked={s.active}
                        onChange={async () => {
                          await api.put(`/report-schedules/${s.id}`, { active: !s.active });
                          loadReportSchedules();
                        }} />
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-success btn-icon btn-sm" title="Run now"
                          onClick={() => handleRunReportNow(s)}><MdPlayArrow /></button>
                        <button className="btn btn-danger btn-icon btn-sm" title="Delete"
                          onClick={() => handleDeleteReportSchedule(s)}><MdDelete /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>}

      {/* ── Movement Reasons ──────────────────────────────────────────────────── */}
      {tab === 'lookups' && <Section
        icon={<MdSwapHoriz style={{ color: 'var(--accent)' }} />}
        title="Movement Reasons"
        subtitle="Used in Asset Movement and bulk move dropdowns. Reasons marked 'requires approval' force movements into a pending state until a manager approves."
      >
        <form onSubmit={handleAddReason} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Name</label>
            <input
              type="text" value={rForm.name}
              onChange={e => setRForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Maintenance"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
            />
          </div>
          <div style={{ flex: '2 1 240px' }}>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description (optional)</label>
            <input
              type="text" value={rForm.description}
              onChange={e => setRForm(f => ({ ...f, description: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8 }}>
            <input type="checkbox" checked={rForm.requires_approval}
              onChange={e => setRForm(f => ({ ...f, requires_approval: e.target.checked }))} />
            Requires approval
          </label>
          <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>
            <MdAdd /> Add
          </button>
        </form>

        {selectedReasonIds.size > 0 && (
          <div style={{ padding: '8px 12px', background: 'var(--surface-2, #f5f5f5)', borderRadius: 6, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedReasonIds.size} selected</span>
            <button className="btn btn-danger btn-sm" onClick={handleBulkDeleteReasons} disabled={busy}>
              <MdDelete /> Delete selected
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setSelectedReasonIds(new Set())}>Clear</button>
          </div>
        )}
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox"
                    checked={reasons.length > 0 && selectedReasonIds.size === reasons.length}
                    onChange={toggleAllReasons} />
                </th>
                <th>Name</th>
                <th>Description</th>
                <th style={{ width: 130 }}>Requires Approval</th>
                <th style={{ width: 90 }}>Active</th>
                <th style={{ width: 90 }}>Used</th>
                <th style={{ width: 130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reasons.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 20 }}>No reasons yet</td></tr>
              ) : reasons.map(r => (
                <tr key={r.id}>
                  <td>
                    <input type="checkbox"
                      checked={selectedReasonIds.has(r.id)}
                      onChange={() => toggleReasonSelect(r.id)} />
                  </td>
                  <td>
                    {rEdit?.id === r.id ? (
                      <input value={rEdit.name}
                        onChange={e => setREdit(m => ({ ...m, name: e.target.value }))}
                        style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, width: '100%' }} />
                    ) : (
                      <span style={{ fontWeight: 500 }}>{r.name}</span>
                    )}
                  </td>
                  <td>
                    {rEdit?.id === r.id ? (
                      <input value={rEdit.description || ''}
                        onChange={e => setREdit(m => ({ ...m, description: e.target.value }))}
                        style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, width: '100%' }} />
                    ) : (
                      <span style={{ color: '#666', fontSize: 13 }}>{r.description || '—'}</span>
                    )}
                  </td>
                  <td>
                    {rEdit?.id === r.id ? (
                      <input type="checkbox" checked={!!rEdit.requires_approval}
                        onChange={e => setREdit(m => ({ ...m, requires_approval: e.target.checked }))} />
                    ) : (
                      <span className={`badge ${r.requires_approval ? 'badge-warning' : 'badge-success'}`}>
                        {r.requires_approval ? 'Yes' : 'No'}
                      </span>
                    )}
                  </td>
                  <td>
                    {rEdit?.id === r.id ? (
                      <input type="checkbox" checked={!!rEdit.active}
                        onChange={e => setREdit(m => ({ ...m, active: e.target.checked }))} />
                    ) : (
                      <span className={`badge ${r.active ? 'badge-success' : 'badge-secondary'}`}>
                        {r.active ? 'Yes' : 'No'}
                      </span>
                    )}
                  </td>
                  <td><span className="badge badge-secondary">{r.used_count}</span></td>
                  <td>
                    {rEdit?.id === r.id ? (
                      <>
                        <button className="btn btn-success btn-icon btn-sm" onClick={handleSaveReasonEdit} disabled={busy} title="Save"><MdSave /></button>
                        <button className="btn btn-outline btn-icon btn-sm" onClick={() => setREdit(null)} disabled={busy} title="Cancel">×</button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-accent btn-icon btn-sm" onClick={() => setREdit({ ...r })} title="Edit"><MdEdit /></button>
                        <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDeleteReason(r)} title="Delete or deactivate"><MdDelete /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>}

      {/* ── Wipe Database ─────────────────────────────────────────────────────── */}
      {tab === 'database' && <Section
        icon={<MdWarning style={{ color: '#d32f2f' }} />}
        title="Wipe Database"
        subtitle="Remove all assets, locations, audits, movements, raw scans, readers and (optionally) non-admin users"
      >
        <div style={{
          background: '#fff5f5', border: '1px solid #ffcdd2',
          borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, color: '#9b1a1a',
        }}>
          ⚠ This action is irreversible. Download a backup first.
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input
            type="checkbox" checked={keepAdmins}
            onChange={e => setKeepAdmins(e.target.checked)}
          />
          <span>Keep admin users (recommended)</span>
        </label>
        <button className="btn btn-danger" onClick={() => setWipeOpen(true)} disabled={busy}>
          <MdDelete /> Wipe Database…
        </button>
      </Section>}

      {/* ── Activity Log ──────────────────────────────────────────────────────── */}
      {tab === 'activity' && <Section
        icon={<MdHistory style={{ color: 'var(--accent)' }} />}
        title="Activity Log"
        subtitle={`${activity.total} total events. Showing ${activity.rows.length} from offset ${actOffset}.`}
      >
        {/* Tabs: separate user CRUD timeline from system / auth / admin events.
            Mirrors the mobile activity log so support questions ("show me what
            happened") can use the same vocabulary across both surfaces. */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderBottom: '1px solid var(--border)' }}>
          {[
            { k: 'activity', label: 'User Activity' },
            { k: 'system',   label: 'System' },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => { setActOffset(0); setActCategory(t.k); }}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                border: 'none',
                borderBottom: actCategory === t.k ? '2px solid var(--accent)' : '2px solid transparent',
                color: actCategory === t.k ? 'var(--accent)' : 'var(--text-secondary)',
                fontWeight: actCategory === t.k ? 700 : 500,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >{t.label}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {isAdmin && (
            <SearchableSelect
              value={actUserId || ''}
              onChange={e => { setActOffset(0); setActUserId(e.target.value ? Number(e.target.value) : null); }}
              style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, minWidth: 180 }}
              title="Drill into one user's activity"
            >
              <option value="">All users</option>
              {actUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.full_name?.trim() || u.username}
                </option>
              ))}
            </SearchableSelect>
          )}
          <input
            placeholder="action contains…"
            value={actFilter.action}
            onChange={e => setActFilter(f => ({ ...f, action: e.target.value }))}
            style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, width: 200 }}
          />
          <input
            placeholder="target type…"
            value={actFilter.target_type}
            onChange={e => setActFilter(f => ({ ...f, target_type: e.target.value }))}
            style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, width: 160 }}
          />
          <button className="btn btn-outline btn-sm" onClick={() => { setActOffset(0); loadActivity(); }}>
            <MdRefresh /> Apply
          </button>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 4 }} />
                <th>When</th>
                <th>What happened</th>
                <th>Status</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {loadingAct ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: 20, color: '#888' }}>Loading…</td></tr>
              ) : activity.rows.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: 20, color: '#888' }}>No activity yet</td></tr>
              ) : activity.rows.map(r => {
                const sev = severityColor(r.severity);
                return (
                  <tr key={r.id} style={{ background: sev.bg }}>
                    <td style={{ background: sev.rail, padding: 0, width: 4 }} />
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{fmtDt(r.created_at)}</td>
                    <td style={{ verticalAlign: 'top' }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: sev.fg, lineHeight: 1.35 }}>
                        {r.summary || `${r.username || 'anon'} · ${r.action}`}
                      </div>
                      {/* Technical detail line — kept for power users / debugging
                          but visually subordinate to the summary above. */}
                      <div style={{ fontSize: 10.5, color: '#90a4ae', marginTop: 2, fontFamily: 'monospace' }}>
                        <span title="action">{r.action}</span>
                        {r.method ? ` · ${r.method}` : ''}
                        {r.path  ? ` · ${r.path.replace(/^\/api/, '')}` : ''}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px', borderRadius: 10,
                        fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
                        background: sev.fg, color: 'white',
                      }}>
                        {r.status_code}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#666', verticalAlign: 'top' }}>{r.ip || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
          <button
            className="btn btn-outline btn-sm"
            disabled={actOffset === 0 || loadingAct}
            onClick={() => setActOffset(Math.max(0, actOffset - actLimit))}
          >Previous</button>
          <span style={{ fontSize: 12, color: '#666' }}>
            {actOffset + 1} – {Math.min(actOffset + activity.rows.length, activity.total)} of {activity.total}
          </span>
          <button
            className="btn btn-outline btn-sm"
            disabled={actOffset + actLimit >= activity.total || loadingAct}
            onClick={() => setActOffset(actOffset + actLimit)}
          >Next</button>
        </div>
      </Section>}

      <ConfirmModal
        open={wipeOpen}
        danger
        title="Wipe entire database?"
        body={
          <>
            <p>This will permanently delete:</p>
            <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
              <li>All assets, locations, audits, audit items, movements</li>
              <li>All raw inventory scan records</li>
              <li>All readers</li>
              <li>{keepAdmins ? 'All non-admin users' : 'All users except you'}</li>
              <li>All activity log entries</li>
            </ul>
            <p style={{ color: '#9b1a1a', fontWeight: 600 }}>This cannot be undone.</p>
          </>
        }
        confirmText="Wipe Database"
        requireText="WIPE"
        onCancel={() => setWipeOpen(false)}
        onConfirm={handleWipe}
      />
    </div>
  );
};

export default Settings;
